import { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { MapPin, Users, Bed, Bath, Star, Calendar, DollarSign, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PropertyDetails() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Failed to fetch property:', error);
      setError('Property not found');
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (user.role !== 'traveler') {
      alert('Only travelers can book properties. Please create a traveler account.');
      return;
    }
    navigate(`/book/${property.id}`);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '20px', color: '#718096' }}>Loading property details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ 
            textAlign: 'center', 
            padding: '60px',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <h2 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
              Property Not Found
            </h2>
            <p style={{ color: '#718096', marginBottom: '24px' }}>
              The property you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/properties" className="btn btn-primary">
              Browse All Properties
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
      <div className="container">
        {/* Property Images */}
        <div style={{ 
          background: 'white', 
          borderRadius: '20px', 
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          marginBottom: '40px'
        }}>
          <div style={{ 
            height: '500px', 
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '8rem',
            position: 'relative'
          }}>
            🏠
            <div style={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              background: 'rgba(255, 255, 255, 0.9)',
              color: '#2d3748',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Star size={20} fill="#ffd700" color="#ffd700" />
              {property.average_rating > 0 ? property.average_rating.toFixed(1) : 'New Property'}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '40px' }}>
          {/* Property Info */}
          <div>
            <div style={{ 
              background: 'white', 
              padding: '40px', 
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              marginBottom: '32px'
            }}>
              <h1 style={{ 
                fontSize: '2.5rem', 
                fontWeight: '700', 
                color: '#2d3748',
                marginBottom: '16px'
              }}>
                {property.title}
              </h1>
              
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', color: '#718096' }}>
                <MapPin size={20} style={{ marginRight: '8px' }} />
                <span style={{ fontSize: '1.1rem' }}>
                  {property.address}, {property.city}, {property.state}, {property.country}
                </span>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '32px', 
                marginBottom: '32px',
                paddingBottom: '24px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Bed size={24} color="#667eea" />
                  <span style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                    {property.bedrooms} Bedroom{property.bedrooms !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Bath size={24} color="#667eea" />
                  <span style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                    {property.bathrooms} Bathroom{property.bathrooms !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Users size={24} color="#667eea" />
                  <span style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                    Up to {property.max_guests} Guests
                  </span>
                </div>
              </div>

              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
                  About this property
                </h3>
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.7', 
                  color: '#4a5568',
                  marginBottom: '24px'
                }}>
                  {property.description}
                </p>
              </div>

              {property.amenities && property.amenities.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
                    Amenities
                  </h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '12px' 
                  }}>
                    {property.amenities.map((amenity, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        padding: '8px 0'
                      }}>
                        <div style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          background: '#667eea' 
                        }}></div>
                        <span style={{ color: '#4a5568' }}>{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Card */}
          <div>
            <div style={{ 
              background: 'white', 
              padding: '32px', 
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              position: 'sticky',
              top: '100px'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ 
                  fontSize: '3rem', 
                  fontWeight: '700', 
                  color: '#667eea',
                  marginBottom: '4px'
                }}>
                  ${property.price_per_night}
                </div>
                <div style={{ fontSize: '1.1rem', color: '#718096' }}>
                  per night
                </div>
              </div>

              <div style={{ 
                background: '#f7fafc', 
                padding: '20px', 
                borderRadius: '12px',
                marginBottom: '24px'
              }}>
                <h4 style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '600', 
                  marginBottom: '12px',
                  color: '#2d3748',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <DollarSign size={20} />
                  Pricing Breakdown
                </h4>
                <div style={{ fontSize: '0.95rem', color: '#4a5568', lineHeight: '1.6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Property rate (per night)</span>
                    <span>${property.price_per_night}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Service fee (6%)</span>
                    <span>6% of total</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Cleaning deposit</span>
                    <span>$150</span>
                  </div>
                  <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                  <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                    Total cost calculated at booking
                  </div>
                </div>
              </div>

              <button 
                onClick={handleBookNow}
                className="btn btn-primary"
                style={{ 
                  width: '100%', 
                  padding: '14px',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Calendar size={20} />
                {user ? 'Book Now' : 'Sign In to Book'}
              </button>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '8px',
                marginTop: '16px',
                fontSize: '0.875rem',
                color: '#718096'
              }}>
                <Shield size={16} />
                Secure booking with ZypeeTravel
              </div>

              {property.total_bookings > 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: '20px',
                  padding: '16px',
                  background: '#f0f8f0',
                  borderRadius: '8px',
                  border: '1px solid #c6f6d5'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#2d7a2d', fontWeight: '500' }}>
                    ✓ {property.total_bookings} successful booking{property.total_bookings !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>  
  );
}

export default PropertyDetails;